import { IconCheck, IconX } from '@developerz/ui';
import {
  createMemo,
  createSignal,
  createUniqueId,
  Index,
  type JSX,
  Match,
  onMount,
  Show,
  Switch,
  untrack,
} from 'solid-js';
import { t } from '../../i18n';
import type { UtilBox } from '../../lib/fleetUtilization';
import type { CardState } from '../../lib/machine-card';
import {
  chosenRole,
  EMPTY_SEND_FORM,
  editForm,
  readLink,
  refusedForm,
  SEND_LINK_MAX,
  SEND_NOTE_MAX,
  type SendChip,
  sendButtonLabel,
  sendChips,
  sendingForm,
  startsLater,
} from '../../lib/send-work';
import type { SendWorkPage } from '../../lib/send-work-page';
import { Field } from '../Field';
import { buttonClass } from '../ui/Button';

// The card's Send work form (#6770, prototype sendHTML, `:2252-2274`): a link, the line
// that recognises it, who gets it, an optional note, and the send. Every verdict is the
// server's (`lib/send-work.ts`): the chips follow the route's own role table, and a refusal
// shows its reason in the recognised line while every field stays as typed.
//
// The send button is `aria-disabled`, never `disabled`, until a link is recognised and a role
// picked, and while the request is in flight ("Sending…"); a press then sends nothing. Each
// submit mints its own `Idempotency-Key`, so a retried attempt of one submit is one write and
// a second submit is a second. Focus lands in the link on open; Escape is Cancel. The chips
// are one radio group: the arrow keys move and pick among the enabled ones.
//
// ONE PRIMARY, NOT BRAND: the Fleet header's "Connect a machine" holds the view's one brand
// button (`FleetHeader.tsx`), so Send is the ink `primary` (deviation, brief.md).

export function SendWork(props: {
  readonly box: UtilBox;
  readonly state: CardState;
  readonly page: SendWorkPage;
  readonly onClose: () => void;
}): JSX.Element {
  const id = createUniqueId();
  const [form, setForm] = createSignal(EMPTY_SEND_FORM);
  const linkText = createMemo(() => form().link);
  const read = createMemo(() => readLink(linkText()));
  const link = createMemo(() => {
    const r = read();
    return r.kind === 'link' ? r.link : null;
  });
  const chips = createMemo(() => sendChips(link(), props.box.roles, t));
  const role = () => chosenRole(chips(), form().role);
  const ready = (): boolean => link() !== null && role() !== null && !form().sending;
  // This form's own opening: every answer to its sends is judged against it (`send`).
  const instance = untrack(props.page.instance);
  let input: HTMLInputElement | undefined;
  let picks: HTMLDivElement | undefined;
  onMount(() => input?.focus());

  const submit = async (event: SubmitEvent): Promise<void> => {
    event.preventDefault();
    const r = role();
    if (!ready() || r === null || link() === null || instance === null) return;
    const f = form();
    const note = f.note.trim() === '' ? {} : { note: f.note };
    setForm(sendingForm(f));
    const outcome = await props.page.send(
      props.box,
      { link: f.link.trim(), role: r, ...note },
      crypto.randomUUID(),
      instance,
    );
    // Only a refusal for THIS instance, while it is the open one, reaches this form's line.
    if (outcome.kind === 'refused') setForm((cur) => refusedForm(cur, outcome.line));
  };

  /** The arrow keys walk the enabled chips, wrapping, and pick the one they land on. */
  const onPickKey = (event: KeyboardEvent): void => {
    const step = { ArrowRight: 1, ArrowDown: 1, ArrowLeft: -1, ArrowUp: -1 }[event.key];
    if (step === undefined) return;
    const open = chips().filter((c) => !c.disabled);
    if (open.length === 0) return;
    event.preventDefault();
    // From the tab stop: the pick, else the first enabled chip, which holds focus before one.
    const at = open.findIndex((c) => c.key === tabStop());
    const next = open[(at + step + open.length) % open.length] as SendChip;
    setForm((f) => editForm(f, { role: next.key }));
    picks?.querySelector<HTMLElement>(`[data-role="${next.key}"]`)?.focus();
  };
  /** The one chip in the tab order: the picked one, else the first enabled. */
  const tabStop = (): string | null => role() ?? chips().find((c) => !c.disabled)?.key ?? null;

  return (
    // biome-ignore lint/a11y/useSemanticElements: a card zone named by its heading (the prototype's `role="group"`, `:2263`); the form inside is the fieldset-free `<form>`, and a fieldset's legend could not hold the Close button.
    <section
      class="mission-zone mission-send"
      role="group"
      aria-labelledby={`${id}-h`}
      onKeyDown={(event) => {
        if (event.key !== 'Escape') return;
        event.preventDefault();
        props.onClose();
      }}
    >
      <p class="mission-zone__label">
        <span id={`${id}-h`}>{t('fleet.send.label')}</span>
        <button
          type="button"
          class={buttonClass({ variant: 'ghost', size: 'icon-sm' })}
          aria-label={t('fleet.send.close')}
          title={t('fleet.send.close')}
          onClick={props.onClose}
        >
          <IconX />
        </button>
      </p>
      <form class="mission-send__form" onSubmit={(event) => void submit(event)}>
        <Field label={t('fleet.send.link_label')} id={`${id}-link`}>
          {(a11y) => (
            <input
              {...a11y}
              ref={input}
              class="input mission-send__link"
              type="text"
              autocomplete="off"
              spellcheck={false}
              maxlength={SEND_LINK_MAX}
              placeholder={t('fleet.send.link_placeholder')}
              value={form().link}
              onInput={(event) => setForm((f) => editForm(f, { link: event.currentTarget.value }))}
            />
          )}
        </Field>
        <p class="mission-send__line" aria-live="polite">
          <Switch>
            <Match when={form().refusal}>
              {(refusal) => <span class="mission-send__crit">{refusal()}</span>}
            </Match>
            <Match when={read().kind === 'malformed'}>
              <span class="mission-send__crit">{t('fleet.send.refused_link_malformed')}</span>
            </Match>
            <Match when={link()}>
              {(l) => (
                <span class="mission-send__ok">
                  <IconCheck />
                  <span class="mission-send__repo">{l().repo}</span>
                  {' · '}
                  {t(l().kind === 'pr' ? 'fleet.send.line_pr' : 'fleet.send.line_issue', {
                    number: String(l().number),
                  })}
                </span>
              )}
            </Match>
            <Match when={read().kind === 'empty'}>
              <span class="mission-send__quiet">{t('fleet.send.line_empty')}</span>
            </Match>
          </Switch>
        </p>
        <div class="mission-send__to">
          <span id={`${id}-to`} class="mission-send__to-label">
            {t('fleet.send.send_to')}
          </span>
          <div
            ref={picks}
            class="mission-send__picks"
            role="radiogroup"
            aria-labelledby={`${id}-to`}
            onKeyDown={onPickKey}
          >
            <Index each={chips()}>
              {(chip) => (
                // biome-ignore lint/a11y/useSemanticElements: the prototype's chip (`:2263`), a button whose `title` says why it is off-limits; a native radio is unnamed by a title and cannot wear the chip. The radiogroup's arrow keys are `onPickKey`'s.
                <button
                  type="button"
                  role="radio"
                  class="mission-send__pick"
                  data-role={chip().key}
                  aria-checked={role() === chip().key}
                  disabled={chip().disabled}
                  tabIndex={tabStop() === chip().key ? 0 : -1}
                  title={chip().title}
                  onClick={() => setForm((f) => editForm(f, { role: chip().key }))}
                >
                  {chip().label}
                  <Show when={chip().off}>
                    {' '}
                    <span class="mission-send__quiet">{t('fleet.send.off')}</span>
                  </Show>
                </button>
              )}
            </Index>
          </div>
        </div>
        <Field label={t('fleet.send.note_label')} id={`${id}-note`}>
          {(a11y) => (
            <textarea
              {...a11y}
              class="textarea mission-send__note"
              rows="2"
              maxlength={SEND_NOTE_MAX}
              placeholder={t('fleet.send.note_placeholder')}
              value={form().note}
              onInput={(event) => setForm((f) => editForm(f, { note: event.currentTarget.value }))}
            />
          )}
        </Field>
        <div class="mission-send__actions">
          <button
            type="submit"
            class={buttonClass({ variant: 'primary', size: 'sm' })}
            aria-disabled={ready() ? undefined : 'true'}
          >
            {sendButtonLabel(role(), form().sending, t)}
          </button>
          <button
            type="button"
            class={buttonClass({ variant: 'ghost', size: 'sm' })}
            onClick={props.onClose}
          >
            {t('fleet.send.cancel')}
          </button>
          <Show when={startsLater(props.state)}>
            <span class="mission-send__quiet">{t('fleet.send.hint_later')}</span>
          </Show>
        </div>
      </form>
    </section>
  );
}
